import React, { useState } from 'react';

const TreeNavigator = ({ treeData, selectedNode, onNodeSelect, onAddChild, loading, selectedTopic, selectedSubtopic,expandedNodes, setExpandedNodes  }) => {
  //const [expandedNodes, setExpandedNodes] = useState(new Set());

  // DEBUG
  console.log('🔍 TreeNavigator rendered. Expanded nodes count:', expandedNodes.size);

  const [searchTerm, setSearchTerm] = useState('');

  // Toggle node expansion
  const toggleExpand = (nodeId) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(nodeId)) {
      newExpanded.delete(nodeId);
    } else {
      newExpanded.add(nodeId);
    }
    setExpandedNodes(newExpanded);
  };

  // Expand all nodes
  const expandAll = () => {
    const allNodeIds = new Set();
    
    const collectNodeIds = (nodes) => {
      nodes.forEach(node => {
        allNodeIds.add(node.id);
        if (node.children && node.children.length > 0) {
          collectNodeIds(node.children);
        }
      });
    };
    
    collectNodeIds(treeData.topics || []);
    setExpandedNodes(allNodeIds);
  };

  // Collapse all nodes
  const collapseAll = () => {
    setExpandedNodes(new Set());
  };

  // Get node icon based on type
  const getNodeIcon = (nodeType) => {
    switch (nodeType) {
      case 'topic': return '📖';
      case 'subtopic': return '📝';
      case 'chunk': return '📄';
      case 'micro_chunk': return '🔍';
      case 'explanation': return '💡';
      default: return '📌';
    }
  };

  // Get available child types for a node
  const getAvailableChildTypes = (nodeType) => {
    switch (nodeType) {
      case 'topic': return ['subtopic']; // Only subtopics under topics
      case 'subtopic': return ['chunk']; // Only chunks under subtopics
      case 'chunk': return ['micro_chunk'];
      case 'micro_chunk': return ['explanation'];
      default: return [];
    }
  };

  // Filter nodes based on search term and selected filters
  const filterNodes = (nodes, searchTerm) => {
    if (!searchTerm && !selectedTopic && !selectedSubtopic) return nodes;
    
    return nodes.filter(node => {
      // First apply topic/subtopic filters
      let passesFilter = true;
      
      if (selectedTopic && node.node_type === 'topic') {
        // Only show the selected topic - strict filter
        return node.id === selectedTopic.topic_id;
      }
      
      if (selectedSubtopic && node.node_type === 'subtopic') {
        // Only show the selected subtopic - strict filter
        return node.id === selectedSubtopic.subtopic_id;
      }
      
      // For non-topic nodes, check if they belong to selected topic/subtopic
      if (selectedTopic && node.node_type !== 'topic') {
        passesFilter = node.topic_id === selectedTopic.topic_id;
      }
      
      if (selectedSubtopic && node.node_type !== 'subtopic') {
        passesFilter = node.subtopic_id === selectedSubtopic.subtopic_id;
      }
      
      // Then apply search filter
      let matchesSearch = true;
      if (searchTerm) {
        matchesSearch = node.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       (node.description && node.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
                       (node.text_en && node.text_en.toLowerCase().includes(searchTerm.toLowerCase())) ||
                       (node.content_en && node.content_en.toLowerCase().includes(searchTerm.toLowerCase()));
      }
      
      return passesFilter && matchesSearch;
    }).map(node => ({
      ...node,
      children: node.children ? filterNodes(node.children, searchTerm) : []
    }));
  };

  // Render a single tree node
  const renderTreeNode = (node, level = 0) => {
    const isSelected = selectedNode && selectedNode.id === node.id;
    const isExpanded = expandedNodes.has(node.id);
    const hasChildren = node.children && node.children.length > 0;
    const availableChildTypes = getAvailableChildTypes(node.node_type);

    return (
      <div key={node.id} className="tree-node-container">
        <div 
          className={`tree-node level-${level} ${isSelected ? 'selected' : ''}`}
          style={{ paddingLeft: `${level * 20 + 12}px` }}
        >
          {/* Expand/Collapse Button */}
          <button
            className={`expand-btn ${hasChildren ? 'has-children' : 'no-children'}`}
            onClick={() => toggleExpand(node.id)}
            disabled={!hasChildren}
          >
            {hasChildren ? (isExpanded ? '▼' : '▶') : '•'}
          </button>

          {/* Node Content */}
          <div 
            className="node-content"
            onClick={() => onNodeSelect(node)}
          >
            <span className="node-name">{node.name}</span>
            <span className="node-type">{node.node_type}</span>
          </div>

          {/* Add Child Buttons */}
          {availableChildTypes.length > 0 && (
            <div className="add-child-buttons">
              {availableChildTypes.map(childType => (
                <button
                  key={childType}
                  className="add-child-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    console.log('Add child button clicked:', { node, childType });
                    onAddChild(node, childType);
                  }}
                  title={`Add ${childType}`}
                >
                  ➕
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Child Nodes */}
        {hasChildren && isExpanded && (
          <div className="tree-children">
            {node.children.map(child => renderTreeNode(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  const filteredTopics = filterNodes(treeData.topics || [], searchTerm);

  return (
    <div className="tree-navigator">
      {/* Header */}
      <div className="tree-header">
        <h3>📚 Content Structure</h3>
        <div className="tree-actions">
          <button 
            className="btn btn-sm btn-secondary"
            onClick={expandAll}
            disabled={loading}
          >
            Expand All
          </button>
          <button 
            className="btn btn-sm btn-secondary"
            onClick={collapseAll}
            disabled={loading}
          >
            Collapse All
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="tree-search">
        <input
          type="text"
          placeholder="🔍 Search content..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        {searchTerm && (
          <button
            className="search-clear"
            onClick={() => setSearchTerm('')}
          >
            ×
          </button>
        )}
      </div>

      {/* Tree Content */}
      <div className="tree-content">
        {loading ? (
          <div className="tree-loading">
            <div className="loading-spinner"></div>
            <p>Loading content...</p>
          </div>
        ) : filteredTopics.length === 0 ? (
          <div className="tree-empty">
            {searchTerm ? (
              <div>
                <p>🔍 No content found for "{searchTerm}"</p>
                <button 
                  className="btn btn-secondary"
                  onClick={() => setSearchTerm('')}
                >
                  Clear Search
                </button>
              </div>
            ) : (
              <div>
                <p>📝 No topics found in this chapter</p>
                <p>Click "Add Topic" to get started</p>
              </div>
            )}
          </div>
        ) : (
          <div className="tree-nodes">
            {filteredTopics.map(topic => renderTreeNode(topic, 0))}
          </div>
        )}
      </div>

      {/* Add Topic Button */}
      <div className="tree-footer">
        <button
          className="btn btn-primary add-topic-btn"
          onClick={() => {
            console.log('Add topic button clicked');
            onAddChild({ id: 'chapter', node_type: 'chapter' }, 'topic');
          }}
          disabled={loading}
        >
          ➕ Add New Topic
        </button>
      </div>
    </div>
  );
};

export default TreeNavigator;